/* Équipe — the members seam (Scope v5 §8, batch 20). One list of people per world: prénom, PIN set or not,
   the establishment they work at. Batch 21 builds the Équipe screens on it; batch 20 needs it for « Votre équipe »
   — the personal supports (a member's own QR + link) that attribute every participation to a person.
   Builder A's Défis and Builder B's Compte et accès read this list; nobody keeps a second one.
   The members row: with an API (VITE_API_URL) the people are the server's (09 §9.1, read at the root into lib/mirror),
   and « Ajouter » and « Réinitialiser son code » are written there. The demo keeps them per world in this browser, as
   before. A member chooses their 4 digits at the till, never in the app (the record, V18). */
import { currentWorld, type World } from '@/mocks/worlds'
import type { Role } from '@/lib/scope'
import { apiWrites, call } from '@/lib/api'
import { mirror, queueWrite, setMirror } from '@/lib/mirror'
import { memberOf, type AppMember } from '@/lib/server'

/* `access` is `none` or the linked login's role — the schema's four values, since every login
   is a member (0021, contract 09 §9.1). An owner or admin appears with that role; the mocks
   below only happen not to seed one. Widened from 'none' | 'staff' | 'manager' on 04/10. */
export interface Member { id: string; firstName: string; locationId: string | null; pinSet: boolean; access: 'none' | Role
  /** with an API, the server's last 30 days (09 §9.1); null on another member's row for a staff login; the demo has none */
  numbers?: MemberNumbers | null }
export interface MemberNumbers { scans30d: number; participations30d: number; giftsValidated30d: number }

function seeds(w: World): Member[] {
  if (w === 'day-one') return []
  const paris9: Member[] = [
    { id: 'm-nadia', firstName: 'Nadia', locationId: 'l0', pinSet: true, access: 'none' },
    { id: 'm-marc', firstName: 'Marc', locationId: 'l0', pinSet: true, access: 'staff' },
    { id: 'm-ines', firstName: 'Inès', locationId: 'l0', pinSet: false, access: 'none' },
  ]
  if (w === 'nine-locations') return [...paris9, { id: 'm-sofia', firstName: 'Sofia', locationId: 'l1', pinSet: true, access: 'manager' }, { id: 'm-yanis', firstName: 'Yanis', locationId: 'l2', pinSet: true, access: 'none' }]
  return paris9
}
const key = () => `bmr.team.${currentWorld()}`
/** The demo's members whose code was reset in this browser (their ids): each chooses again at the till. */
const resetKey = () => `bmr.team-reset.${currentWorld()}`
function demoResets(): string[] {
  try { const l = JSON.parse(localStorage.getItem(resetKey()) ?? '[]') as unknown; return Array.isArray(l) ? l.filter((x): x is string => typeof x === 'string') : [] } catch { return [] }
}
/** Équipe's people: with an API the server's, else the world's (demoTeam). */
export const getMembers = (): Member[] => (apiWrites ? mirror().members : demoTeam())
/** The world's people in the demo: its seeds and what this browser added, less the codes reset here (the mock server
    seeds its members from them). */
export function demoTeam(): Member[] {
  const resets = demoResets()
  return worldMembers().map(m => (resets.includes(m.id) ? { ...m, pinSet: false } : m))
}
function worldMembers(): Member[] {
  try { const raw = localStorage.getItem(key()); if (raw) { const l = JSON.parse(raw) as Member[]; if (Array.isArray(l)) return [...seeds(currentWorld()), ...l] } } catch { /* fine */ }
  return seeds(currentWorld())
}
/** « Ajouter » (09 §9.1). The demo keeps the member in this browser at once. With an API the server makes it
    (POST /v1/app/members), in turn with the other writes, and the member is listed once it answers, under the server's
    id: a QR asked right after names a member the server holds. A refusal comes back to the caller. */
export async function addMember(firstName: string, locationId: string | null): Promise<Member> {
  if (apiWrites) {
    const m = memberOf(await queueWrite(() => call<AppMember>('POST', '/app/members', { firstName: firstName.trim(), locationId })))
    setMirror({ members: [...mirror().members.filter(x => x.id !== m.id), m] })
    return m
  }
  const m: Member = { id: `m-${Date.now().toString(36)}`, firstName: firstName.trim(), locationId, pinSet: false, access: 'none' }
  try { const raw = localStorage.getItem(key()); const l = raw ? (JSON.parse(raw) as Member[]) : []; localStorage.setItem(key(), JSON.stringify([...l, m])) } catch { /* fine */ }
  return m
}
/** « Réinitialiser son code » (POST /v1/app/members/:id/pin/reset; the owner, an admin, or a manager for a member of
    their establishments). The server clears the code and answers nothing: the old one no longer works, and the member
    chooses a new one at the till, the next time. The demo only marks it. */
export async function resetMemberPin(id: string): Promise<void> {
  if (apiWrites) {
    await queueWrite(() => call<void>('POST', `/app/members/${encodeURIComponent(id)}/pin/reset`, {}))
    setMirror({ members: mirror().members.map(m => (m.id === id ? { ...m, pinSet: false } : m)) })
    return
  }
  const resets = demoResets()
  try { if (!resets.includes(id)) localStorage.setItem(resetKey(), JSON.stringify([...resets, id])) } catch { /* fine */ }
}
/** Default 1 (members row): a member of the whole workspace (no establishment: the owner's own) works in every one. */
export const worksAt = (m: Member, locationId: string): boolean => m.locationId === null || m.locationId === locationId
export const membersIn = (locationId: string) => getMembers().filter(m => worksAt(m, locationId))
/** Where a member works, in the app's words: the establishment's name, or « Tous les établissements » (the switcher's and
    the roues' label) for a member of the whole workspace. */
export const memberPlace = (m: Member, nameOf: (id: string) => string): string =>
  m.locationId === null ? 'Tous les établissements' : nameOf(m.locationId)
